import PropTypes from 'prop-types';
import { useRef, useMemo, useEffect, useReducer, useCallback } from 'react';
import {
  getAuth,
  signOut,
  updatePassword,
  signInWithPopup,
  EmailAuthProvider,
  onAuthStateChanged,
  GoogleAuthProvider,
  sendEmailVerification,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  reauthenticateWithCredential,
  createUserWithEmailAndPassword,
} from 'firebase/auth';

import { firebaseApp } from './lib';
import { AuthContext } from './auth-context';
import { identifyClarityUser } from '../../utils/clarity';
import { identifyUser, resetSession } from '../../utils/crisp';
import { firebaseErrorMessage } from '../firebase-error-message';
import { getUser, createUser, updateUser } from '../../redux/slices/user';
import { tellExtensionWhenSignedInUserChanges } from '../../utils/extension-auth-sync';
import { trackEvent, resetPostHog, ANALYTICS_EVENTS, identifyPostHogUser } from '../../utils/posthog';



const AUTH = getAuth(firebaseApp);

/**
 * Whether the account signs in with Google and nothing else, so it has no Voolist password: a seller
 * who signed up with Google has none unless they also set one up with their email. Read from Firebase's
 * own record of how the account signs in, because the user this context hands out is replaced by the
 * database's copy whenever it is refreshed, and that copy doesn't carry it.
 */
function signsInWithGoogleOnly (firebaseUser) {
  const providerIds = (firebaseUser?.providerData || []).map(({ providerId }) => providerId);
  return providerIds.length > 0 && providerIds.every((providerId) => providerId === GoogleAuthProvider.PROVIDER_ID);
}

const initialState = {
  user: null,
  loading: true,
};

const reducer = (state, action) => {
  if (action.type === 'INITIAL') {
    return {
      loading: false,
      user: action.payload.user,
    };
  }
  return state;
};

export function AuthProvider ({ children }) {
  const [state, dispatch] = useReducer(reducer, initialState);
  // While the Google or sign-up flow is signing someone in, it brings their profile up to date itself, so the
  // Firebase listener below leaves that sign-in to it.
  const signInFlowRunningRef = useRef(false);

  const syncUserWithDatabase = useCallback(async (firebaseUser, role) => {
    // Check if firebaseUser is null
    if (!firebaseUser) {
      return null;
    }

    try {

      let userProfile = await getUser();

      if (!userProfile) {

        const assignedRole = role || 'user';

        // Create new user in database if not exists  
        userProfile = await createUser({
          uid: firebaseUser.uid,
          email: firebaseUser.email,
          displayName: firebaseUser.displayName,
          photoURL: firebaseUser.photoURL,
          emailVerified: firebaseUser.emailVerified,
          lastLoginAt: new Date().toISOString(),
          role: assignedRole,
        });
      } else {
        // Update existing user
        const updateData = {
          emailVerified: firebaseUser.emailVerified,
          lastLoginAt: new Date().toISOString(),
        };

        // Only set photoURL from Firebase if user doesn't have a custom one in DB
        // This prevents overwriting custom profile pictures (uploaded to R2) with Firebase's photoURL
        if (!userProfile.photoURL && firebaseUser.photoURL) {
          updateData.photoURL = firebaseUser.photoURL;
        }

        // If a role is provided (e.g., Shopify installed flow with 'multi_store' role)
        // Backend will validate and only allow safe role transitions:
        // - 'user' -> 'multi_store' ✅ (Shopify onboarding)
        // - Any changes to/from 'admin' ❌ (protected, admin-only)
        if (role) {
          updateData.role = role;
        }

        userProfile = await updateUser(updateData);
      }

      // eslint-disable-next-line consistent-return
      return {
        ...firebaseUser,
        ...userProfile,
      };
    } catch (error) {
      // eslint-disable-next-line consistent-return
      return null;
    }
  }, []);

  // Follows who is signed in, with one Firebase listener for as long as the app is open. Firebase tells it of
  // every change, so a second listener would bring the seller's profile up to date a second time.
  useEffect(
    () =>
      onAuthStateChanged(AUTH, async (user) => {
        if (user && signInFlowRunningRef.current) return;
        if (!user || !user.emailVerified) {
          dispatch({ type: 'INITIAL', payload: { user: null } });
          return;
        }

        const syncedUser = await syncUserWithDatabase(user);

        // Identify the user with Clarity for session stitching
        identifyClarityUser(syncedUser);
        identifyPostHogUser(syncedUser);

        dispatch({ type: 'INITIAL', payload: { user: syncedUser } });
      }),
    [syncUserWithDatabase]
  );

  // Every login, logout and switch of account, wherever it happens — this tab, another tab, Firebase
  // signing the user out — reaches the Voolist extension, so it never keeps working for an account the
  // seller has left.
  useEffect(() => tellExtensionWhenSignedInUserChanges((listener) => onAuthStateChanged(AUTH, listener)), []);

  const login = useCallback(
    async (email, password, roleFromLogin = null) => {
      const userCredential = await signInWithEmailAndPassword(AUTH, email, password).catch((err) => {
        trackEvent(ANALYTICS_EVENTS.LOGIN_FAILED, { method: 'password', code: err.code });
        throw new Error(firebaseErrorMessage(err, "Couldn't sign you in. Try again."));
      });

      // Check if email is verified
      if (!userCredential.user.emailVerified) {
        // Instead of throwing an error, return a special response to redirect to verification
        trackEvent(ANALYTICS_EVENTS.LOGIN_FAILED, { method: 'password', code: 'email_not_verified' });
        throw new Error('EMAIL_NOT_VERIFIED');
      }

      // Sync the user profile with database and get the merged profile
      const syncedUser = await syncUserWithDatabase(userCredential.user, roleFromLogin);

      // Immediately update the auth state so route guards pick up the change
      dispatch({
        type: 'INITIAL',
        payload: { user: syncedUser },
      });

      // Identify the user with Crisp chat and Clarity
      identifyUser(syncedUser);
      identifyClarityUser(syncedUser);
      identifyPostHogUser(syncedUser);
      // Fired after identify so the event lands on the logged-in person, not an anonymous id.
      trackEvent(ANALYTICS_EVENTS.LOGIN_SUCCEEDED, { method: 'password' });
    },
    [syncUserWithDatabase, dispatch]
  );

  const loginWithGoogle = useCallback(
    async (roleFromRegister = null) => {
      // Ends however the sign-in ends: a closed or blocked popup must not leave it marked as running.
      signInFlowRunningRef.current = true;
      try {
        const provider = new GoogleAuthProvider();
        const userCredential = await signInWithPopup(AUTH, provider);

        // Sync the user profile with database and get the merged profile
        const syncedUser = await syncUserWithDatabase(userCredential.user, roleFromRegister);

        // Immediately update the auth state so route guards pick up the change
        dispatch({
          type: 'INITIAL',
          payload: { user: syncedUser },
        });

        // Identify the user with Crisp chat and Clarity
        identifyUser(syncedUser);
        identifyClarityUser(syncedUser);
        identifyPostHogUser(syncedUser);
        trackEvent(ANALYTICS_EVENTS.LOGIN_SUCCEEDED, { method: 'google' });
      } finally {
        signInFlowRunningRef.current = false;
      }
    },
    [syncUserWithDatabase, dispatch]
  );

  const register = useCallback(async (email, password, firstName, lastName, role = "user") => {
    // Ends however the sign-up ends: a refused email must not leave it marked as running.
    signInFlowRunningRef.current = true;
    try {
      const userCredential = await createUserWithEmailAndPassword(AUTH, email, password).catch(err => {

        throw new Error(firebaseErrorMessage(err, "Couldn't create your account. Try again."));
      });

      await sendEmailVerification(userCredential.user);

      // Store timestamp when verification email was sent
      localStorage.setItem('lastVerificationEmailSent', Date.now().toString());

      await createUser({
        uid: userCredential.user.uid,
        email,
        role,
        displayName: `${firstName} ${lastName}`,
        emailVerified: false,
        lastLoginAt: new Date().toISOString(),
      });

      // Identify the user with Crisp chat and Clarity
      identifyUser(userCredential.user);
      identifyClarityUser(userCredential.user);
    } finally {
      signInFlowRunningRef.current = false;
    }
  }, []);

  const logout = useCallback(async () => {
    // Fired before resetPostHog, or the event would be attributed to an anonymous id.
    trackEvent(ANALYTICS_EVENTS.LOGOUT);
    await signOut(AUTH);
    // Reset Crisp session on logout to clear tracking
    resetSession();
    identifyUser(null);
    // Clear PostHog identity so the next user isn't merged into this one
    resetPostHog();
  }, []);

  // Asking for a reset link never tells anyone whether an email has a Voolist account, so an email
  // with no account is answered exactly like one with an account.
  const forgotPassword = useCallback(async (email) => {
    try {
      await sendPasswordResetEmail(AUTH, email);
    } catch (error) {
      if (error?.code !== 'auth/user-not-found') throw error;
    }
    trackEvent(ANALYTICS_EVENTS.PASSWORD_RESET_REQUESTED);
    // Clear Crisp user data
    identifyUser(null);
  }, []);

  const resendEmailVerification = useCallback(async () => {
    try {
      const user = AUTH.currentUser;
      if (!user) {
        throw new Error("You're signed out. Sign in again to get a new email.");
      }

      if (user.emailVerified) {
        throw new Error('Your email is already confirmed. Sign in to continue.');
      }

      // Cooldown to prevent spamming resend
      const lastSentTime = localStorage.getItem('lastVerificationEmailSent');
      if (lastSentTime) {
        const timeSent = parseInt(lastSentTime, 10);
        const cooldownEnd = timeSent + (60 * 1000); // 60 second cooldown
        const now = Date.now();

        if (now < cooldownEnd) {
          const remainingSeconds = Math.ceil((cooldownEnd - now) / 1000);
          throw new Error(`You can request a new email in ${remainingSeconds} second${remainingSeconds !== 1 ? 's' : ''}.`);
        }
      }

      await sendEmailVerification(user);

      // Store timestamp when email was sent
      localStorage.setItem('lastVerificationEmailSent', Date.now().toString());

      return { success: true, message: 'Verification email sent successfully' };
    } catch (error) {
      throw new Error(error.message || "Couldn't send the email. Try again.");
    }

  }, []);

  const changePassword = useCallback(async (oldPassword, newPassword) => {
    try {
      const user = AUTH.currentUser;
      if (!user) {
        throw new Error("You're signed out. Sign in again, then change your password.");
      }

      if (!user.email) {
        throw new Error("Couldn't change your password. Log out, sign in again, then try again.");
      }

      // Create credential with user's email and old password
      const credential = EmailAuthProvider.credential(user.email, oldPassword);

      // Re-authenticate the user with their old password
      await reauthenticateWithCredential(user, credential);

      // Update password to the new one
      await updatePassword(user, newPassword);

      return { success: true, message: 'Password updated successfully' };
    } catch (error) {
      // Handle specific Firebase errors
      if (error.code === 'auth/wrong-password' || error.code === 'auth/invalid-credential') {
        throw new Error('Current password is incorrect');
      } else if (error.code === 'auth/weak-password') {
        throw new Error('New password is too weak');
      } else {
        throw new Error(firebaseErrorMessage(error, "Couldn't change your password. Try again."));
      }
    }
  }, []);

  // Re-fetch the current user from the DB and push it into the auth context so changes made in
  // Account Settings (e.g. timezone) propagate live to every component reading `user` — no reload.
  const refreshUser = useCallback(async () => {
    try {
      const fresh = await getUser();
      dispatch({ type: 'INITIAL', payload: { user: fresh } });
      return fresh;
    } catch (error) {
      return null;
    }
  }, []);

  const checkAuthenticated = state.user?.emailVerified ? 'authenticated' : 'unauthenticated';
  const status = state.loading ? 'loading' : checkAuthenticated;

  const memoizedValue = useMemo(
    () => ({
      user: state.user,
      method: 'firebase',
      loading: status === 'loading',
      isAuthenticated: status === 'authenticated',
      isUnauthenticated: status === 'unauthenticated',
      // Read again whenever the signed-in user changes, which is when Firebase's record can change.
      signsInWithGoogleOnly: signsInWithGoogleOnly(AUTH.currentUser),
      login,
      logout,
      register,
      forgotPassword,
      loginWithGoogle,
      resendEmailVerification,
      changePassword,
      refreshUser,
    }),
    [
      status,
      state.user,
      login,
      logout,
      register,
      forgotPassword,
      loginWithGoogle,
      resendEmailVerification,
      changePassword,
      refreshUser,
    ]
  );

  return <AuthContext.Provider value={memoizedValue}>{children}</AuthContext.Provider>;
}

AuthProvider.propTypes = {
  children: PropTypes.node,
};